import { trackEvent } from '@shared/lib/tracking'
import { useUserContext } from '@vp/ubik-context'
import { useCallback } from 'react'
import { buildDestinationPath, buildPageName, PAGE_DEFAULTS, TRACKING_EVENTS } from '../config/tracking.config'
import { useFormData } from '../context/form-data-context'
import { usePageFlow } from '../context/page-flow-context'

interface UseTrackingReturn {
  /** Track a page view for the current step */
  trackPageView: () => void

  /** Track the "Next" / continue navigation */
  trackNavNext: (navigationDetail?: string) => void
}

/**
 * Provides convenience methods for firing tracking events
 * scoped to the current step and channel context.
 */
export const useTracking = (): UseTrackingReturn => {
  const { locale } = useUserContext()
  const { currentStepIndex, channel } = usePageFlow()
  const { formData } = useFormData()

  const trackPageView = useCallback(() => {
    trackEvent(
      'page',
      {
        pageName: buildPageName(currentStepIndex),
        locale,
        channel,
      },
      'page',
    )
  }, [currentStepIndex, locale])

  const isSubmitStep = currentStepIndex === 1
  const optInRecording = formData.channel === 'callback' ? formData.optInRecording : undefined

  const trackNavNext = useCallback(
    (navigationDetail?: string) => {
      if (isSubmitStep) {
        if (channel === 'callback') {
          const eventDetail =
            formData.channel === 'callback' ? (formData.optInRecording ? 'Request Callback - Recorded' : 'Request Callback - Not Recorded') : undefined

          trackEvent(TRACKING_EVENTS.POP_UP_CLICKED, {
            category: 'Pop-Up',
            label: 'Callback Pop-Up',
            pageSection: 'Help Center',
            pageStage: 'Customer Care',
            pageName: 'Home Page:Help Center',
            locale,
            requestId: '',
            ...(eventDetail && { eventDetail }),
          })
        }
        return
      }

      const intent = formData.intentId
      const detail = navigationDetail || (intent ? `Contact Reason Selected:${intent}` : 'Next Step')

      trackEvent(
        TRACKING_EVENTS.NAVIGATION_CLICKED,
        {
          category: PAGE_DEFAULTS.CATEGORY,
          pageName: buildPageName(currentStepIndex),
          destinationPagePath: buildDestinationPath(currentStepIndex + 1, channel),
          navigationDetail: detail,
          locale,
          channel,
        },
        'nav',
      )
    },
    [isSubmitStep, currentStepIndex, channel, locale, formData.intentId, optInRecording],
  )

  return {
    trackPageView,
    trackNavNext,
  }
}
